import React from 'react';
import { usedBy, plural } from '../../../models/LibraryModel';
import ListTable from '../shared/ListTable';
import Timestamp from '../shared/Timestamp';
import LibraryStatusChip, { libraryStatusLabel } from './LibraryStatusChip';
import LibraryDocumentMenu, { ArchivedTip } from './LibraryDocumentMenu';

function usedByCell(doc) {
  const n = usedBy(doc);
  return n ? `${n} ${plural(n, 'scenario', 'scenarios')}` : 'Not in use';
}

// PRD-1700: the handoff's Library documents table. Name is pinned; every
// column with values sorts from its header.

// A cut-off name or file shows in full in the ResUI Tooltip (the table marks
// it), and the date's long form, with its zone, in Timestamp's: never a title
// (ResUI Design Decisions, Tables and Tooltip). The nearest tip wins, so an
// archived row shows one at a time: the cell's over its text, "Archived" elsewhere.
const COLUMNS = [
  {
    key: 'name',
    title: 'Name',
    width: '240px',
    sort: 'text',
    value: (doc) => doc.name,
    render: (doc) => (
      <span data-doc-focus={doc.id} tabIndex={-1} className="library-cell library-cell--strong list-table-cut">{doc.name}</span>
    ),
  },
  {
    key: 'file',
    title: 'File',
    width: 'minmax(180px, 1fr)',
    sort: 'text',
    value: (doc) => doc.fileName,
    render: (doc) => <span className="library-cell list-table-cut">{doc.fileName}</span>,
  },
  { key: 'status', title: 'Status', width: '132px', sort: 'text', value: libraryStatusLabel, render: (doc) => <LibraryStatusChip doc={doc} /> },
  {
    key: 'uploaded',
    title: 'Uploaded',
    width: '120px',
    sort: 'date',
    value: (doc) => Date.parse(doc.uploadedAt) || null,
    render: (doc) => <span className="library-cell"><Timestamp value={doc.uploadedAt} /></span>,
  },
  { key: 'used', title: 'Used by', width: '120px', sort: 'number', value: usedBy, render: (doc) => <span className="library-cell">{usedByCell(doc)}</span> },
];

// The archived tooltip comes from the row's data-tip, so the scroller can't
// clip it; ArchivedTip stays, hidden, as the menu trigger's description.
const archivedRowProps = (doc) => (doc.archived
  ? { className: 'is-archived', 'data-tip': 'Archived', 'data-tip-note': 'Unarchive to use it' }
  : {});

/**
 * The list view: a panel holding the shared List table (PRD-1700). Its
 * headers are the sort; the toolbar's Sort shows in the grid view only.
 */
function LibraryList({ documents, label, actions, sort, onSortChange, resetKey, focusId }) {
  const columns = [
    ...COLUMNS,
    {
      key: 'actions',
      title: <span className="library-sr-only">Actions</span>,
      label: 'Actions',
      width: '48px',
      cellClassName: 'library-cell--menu',
      render: (doc) => (
        <>
          <LibraryDocumentMenu doc={doc} actions={actions} />
          <ArchivedTip doc={doc} />
        </>
      ),
    },
  ];
  return (
    <div className="library-panel">
      <ListTable
        label={label}
        columns={columns}
        rows={documents}
        rowKey={(doc) => doc.id}
        rowProps={archivedRowProps}
        sort={sort}
        onSortChange={onSortChange}
        resetKey={resetKey}
        focusRowKey={focusId}
      />
    </div>
  );
}

export default LibraryList;
